import { captureException, captureMessage } from '@sentry/core';
import { Subscription } from 'apollo-client/util/Observable';

import { chatClient } from 'src/config/Apollo';
import { RootState } from 'src/config/Store';
import { PresenceState } from 'src/constants';
import {
  onlineForLiveCoachingSubscriptionGql,
  onlineForMyClientsSubscriptionGql,
  onProviderDashboardSubscriptionGql,
} from 'src/constants/presenceQueries';

interface PresenceRematchState {
  state: PresenceState;
  onProviderDashboardSubscription: Subscription | null;
  presenceSubscription: Subscription | null;
}

const getDefaultState = (): PresenceRematchState => {
  return {
    state: PresenceState.Online,
    onProviderDashboardSubscription: null,
    presenceSubscription: null,
  };
};

export const presence = {
  state: getDefaultState(),
  reducers: {
    setState: (currentState: PresenceRematchState, nextState: PresenceState) => ({
      ...currentState,
      state: nextState,
    }),
    'featureFlags/setFlagValue': (
      currentState: PresenceRematchState,
      flagName: string,
      flagValue: boolean,
    ) => {
      if (flagName === 'hideLiveChat' && flagValue === true) {
        return {
          ...currentState,
          state: PresenceState.Offline,
        };
      }

      return currentState;
    },
    'user/reset': (currentState: PresenceRematchState) => {
      return getDefaultState();
    },
    setProviderDashboardSubscription: (
      currentState: PresenceRematchState,
      nextSubsciption: Subscription,
    ) => ({
      ...currentState,
      onProviderDashboardSubscription: nextSubsciption,
    }),
    setPresenceSubscription: (
      currentState: PresenceRematchState,
      nextSubsciption: Subscription,
    ) => ({
      ...currentState,
      presenceSubscription: nextSubsciption,
    }),
  },
  effects: (dispatch) => ({
    startOnProviderDashboardSubscription(_payload: undefined): void {
      const observable = chatClient.subscribe({
        query: onProviderDashboardSubscriptionGql,
      });

      const subscription = observable.subscribe({
        error: captureException,
      });
      dispatch.presence.setProviderDashboardSubscription(subscription);
    },
    updatePresence(payload: PresenceState, rootState: RootState): void {
      // Unsubscribe from the old presence state.
      if (rootState.presence.presenceSubscription) {
        rootState.presence.presenceSubscription.unsubscribe();
      }

      const gqlQueries = {
        [PresenceState.LiveCoaching]: onlineForLiveCoachingSubscriptionGql,
        [PresenceState.Online]: onlineForMyClientsSubscriptionGql,
      };

      dispatch.presence.setState(payload);

      if (payload === PresenceState.Offline) {
        return;
      }

      const subscriptionGql = gqlQueries[payload];
      if (!subscriptionGql) {
        captureMessage(`Could not find subscription for presence state ${payload}`);
        return;
      }

      const observable = rootState.network.client.subscribe({
        query: subscriptionGql,
      });

      const subscription = observable.subscribe({ error: captureException });
      dispatch.presence.setPresenceSubscription(subscription);
    },
  }),
};
